'use client';

import { Button, Grid, SelectField, TextField } from '@petsmart-ui/sparky';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CA_PROVINCES, getNextApiRoute, US_STATES, zipCodeSetter } from '@libs/common-next';
import { HttpMethod, isLocaleCA, removeSpecialCharacters } from '@libs/core';

import { CountryTarget } from '../../constants';
import { useFetchWithSnackbarErrorHandling } from '../../hooks/useFetchWithSnackbarErrorHandling';
import { StreetAddressLine } from './StreetAddressLine';
import type {
    AddressFormValues,
    FormatAddressResponseInterface,
    SuggestedAddressInterface,
    SuggestionResponseInterface,
} from './types';
import {
    applyFormattedAddressToFormik,
    applyFormattedAddressToRHF,
    createSuggestedAddressSelectionHandler,
    getFieldErrorMessage,
} from './utils';
import { provinceRequiredMsg, stateRequiredMsg } from './validationErrors';
import './AddressForm.scss';

export type {
    FormatAddressResponseInterface,
    FormattedAddressFieldInterface,
    SuggestedAddressInterface,
    SuggestionResponseInterface,
} from './types';

type AddressFormProps = Readonly<{
    addressSuggestionRoute: string;
    aptFieldDefaultOpen?: boolean;
    countryTarget?: CountryTarget;
    formLibrary?: 'formik' | 'rhf';
}>;

export function AddressForm({ aptFieldDefaultOpen, countryTarget, formLibrary = 'formik' }: AddressFormProps) {
    const [suggestedAddresses, setSuggestedAddresses] = useState<SuggestedAddressInterface[] | null>(null);
    const [aptFieldOpen, setAptFieldOpen] = useState(Boolean(aptFieldDefaultOpen));
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { fetchWithSnackbarErrorHandling } = useFetchWithSnackbarErrorHandling();

    const statesOrProvinces = useMemo(() => {
        if (countryTarget === CountryTarget.BOTH) {
            const combined = [...US_STATES, ...CA_PROVINCES];
            return combined.sort((a, b) => a.name.localeCompare(b.name));
        }
        return isLocaleCA() ? CA_PROVINCES : US_STATES;
    }, [countryTarget]);

    useEffect(() => {
        if (aptFieldDefaultOpen) {
            setAptFieldOpen(true);
        }
    }, [aptFieldDefaultOpen]);

    const checkAddressSuggestions = useCallback(
        async (suggestAddressInput: string): Promise<SuggestionResponseInterface | null> => {
            try {
                const response = await fetchWithSnackbarErrorHandling<SuggestionResponseInterface>(
                    `${getNextApiRoute('address/suggest-address-edq')}?query=${suggestAddressInput}&country=${countryTarget}`, // NOSONAR - deprecated function is still required for existing API routes
                    {
                        method: HttpMethod.GET,
                    }
                );

                if (response === null) {
                    return null;
                }

                setSuggestedAddresses(response.results);
                return response;
            } catch {
                return null;
            } finally {
                setLoading(false);
            }
        },
        [fetchWithSnackbarErrorHandling, countryTarget]
    );

    const formatSuggestedAddress = useCallback(
        async (suggestedAddressId: string, country: string | null): Promise<FormatAddressResponseInterface | null> => {
            try {
                return await fetchWithSnackbarErrorHandling<FormatAddressResponseInterface>(
                    `${getNextApiRoute('address/format-address-edq')}?id=${suggestedAddressId}&country=${country}`, // NOSONAR - deprecated function is still required for existing API routes
                    {
                        method: HttpMethod.GET,
                    }
                );
            } catch {
                return null;
            }
        },
        [fetchWithSnackbarErrorHandling]
    );

    const handleSuggestedAddressClick = async (suggestedAddress: SuggestedAddressInterface) => {
        const { searchParams } = new URL(suggestedAddress.format);
        const id = searchParams.get('id');
        const country = searchParams.get('country');

        if (!id) {
            setDropdownIsOpen(false);
            return null;
        }

        const formatAddressResponse = await formatSuggestedAddress(id, country);

        setDropdownIsOpen(false);
        return formatAddressResponse;
    };

    if (formLibrary === 'rhf') {
        return (
            <AddressFormRHF
                aptFieldOpen={aptFieldOpen}
                countryTarget={countryTarget}
                statesOrProvinces={statesOrProvinces}
                dropdownIsOpen={dropdownIsOpen}
                suggestedAddresses={suggestedAddresses}
                loading={loading}
                setLoading={setLoading}
                checkAddressSuggestions={checkAddressSuggestions}
                setDropdownIsOpen={setDropdownIsOpen}
                setSuggestedAddresses={setSuggestedAddresses}
                setAptFieldOpen={setAptFieldOpen}
                handleSuggestedAddressClick={handleSuggestedAddressClick}
            />
        );
    }

    return (
        <AddressFormFormik
            aptFieldOpen={aptFieldOpen}
            countryTarget={countryTarget}
            statesOrProvinces={statesOrProvinces}
            dropdownIsOpen={dropdownIsOpen}
            suggestedAddresses={suggestedAddresses}
            loading={loading}
            setLoading={setLoading}
            checkAddressSuggestions={checkAddressSuggestions}
            setDropdownIsOpen={setDropdownIsOpen}
            setSuggestedAddresses={setSuggestedAddresses}
            setAptFieldOpen={setAptFieldOpen}
            handleSuggestedAddressClick={handleSuggestedAddressClick}
        />
    );
}

type RenderAddressFormProps = {
    aptFieldOpen: boolean;
    countryTarget?: CountryTarget;
    statesOrProvinces: { abbr: string; name: string }[];
    dropdownIsOpen: boolean;
    suggestedAddresses: SuggestedAddressInterface[] | null;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    checkAddressSuggestions: (suggestAddressInput: string) => Promise<SuggestionResponseInterface | null>;
    setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSuggestedAddresses: React.Dispatch<React.SetStateAction<SuggestedAddressInterface[] | null>>;
    setAptFieldOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleSuggestedAddressClick: (
        suggestedAddress: SuggestedAddressInterface
    ) => Promise<FormatAddressResponseInterface | null>;
};

function AddressFormFormik({
    aptFieldOpen,
    countryTarget,
    statesOrProvinces,
    dropdownIsOpen,
    suggestedAddresses,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses,
    setAptFieldOpen,
    handleSuggestedAddressClick,
}: Readonly<RenderAddressFormProps>) {
    const {
        values,
        handleChange,
        handleBlur,
        touched: { address: touchedAddress },
        errors: { address: addressErrors },
        setValues,
        setFieldValue,
    } = useFormikContext<AddressFormValues>();

    const addressValues = values.address;

    useEffect(() => {
        if (addressValues.apt) {
            setAptFieldOpen(true);
        }
    }, [addressValues.apt, setAptFieldOpen]);

    return (
        <Grid gap="sm">
            <Grid.Item>
                <StreetAddressLine
                    formLibrary="formik"
                    dropdownIsOpen={dropdownIsOpen}
                    suggestedAddresses={suggestedAddresses}
                    handleSuggestedAddressClick={createSuggestedAddressSelectionHandler(
                        handleSuggestedAddressClick,
                        formattedAddress => {
                            setAptFieldOpen(!!formattedAddress.apt);
                            applyFormattedAddressToFormik(formattedAddress, values, setValues);
                        }
                    )}
                    loading={loading}
                    setLoading={setLoading}
                    checkAddressSuggestions={checkAddressSuggestions}
                    setDropdownIsOpen={setDropdownIsOpen}
                    setSuggestedAddresses={setSuggestedAddresses}
                />
            </Grid.Item>
            <Grid.Item>
                {aptFieldOpen ? (
                    <TextField
                        data-testid="apt"
                        data-cs-mask
                        name="address.apt"
                        type="text"
                        label="Apt/suite (optional)"
                        value={addressValues.apt}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                ) : (
                    <Button
                        data-testid="apt-btn"
                        aria-expanded="true"
                        className="sparky-u-width-fit-content sparky-u-typography-preset-7"
                        variant="link"
                        iconName="plus"
                        iconSize="md"
                        text="Add apt/suite/other"
                        type="button"
                        onClick={() => setAptFieldOpen(true)}
                    />
                )}
            </Grid.Item>

            <Grid.Item>
                <Grid variant="side-by-side" gap="sm">
                    <Grid.Item>
                        <TextField
                            data-testid="zipCode"
                            data-cs-mask
                            wrapperTestId="zipCode-field"
                            name="address.zipCode"
                            type="text"
                            label={isLocaleCA() ? 'Postal code' : 'Zip code'}
                            value={addressValues.zipCode}
                            isError={touchedAddress?.zipCode && !!addressErrors?.zipCode}
                            onBlur={handleBlur}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                ev.target.value = removeSpecialCharacters(ev.target.value);
                                zipCodeSetter(ev, 'address.zipCode', (_, value) => {
                                    setFieldValue('address.zipCode', value);
                                });
                            }}
                            fieldNote={
                                touchedAddress?.zipCode ? getFieldErrorMessage(addressErrors?.zipCode) : undefined
                            }
                            maxLength={countryTarget !== CountryTarget.BOTH && !isLocaleCA() ? 5 : undefined}
                        />
                    </Grid.Item>
                    <Grid.Item data-testid="state-field">
                        <SelectField
                            className={addressValues.state ? 'sparky-is-filled' : undefined}
                            data-testid="state"
                            data-cs-mask
                            name="address.state"
                            items={statesOrProvinces?.map(({ abbr, name }) => ({
                                label: name,
                                value: abbr,
                                disabled: false,
                            }))}
                            label={isLocaleCA() ? 'Province' : 'State'}
                            value={addressValues.state}
                            isError={touchedAddress?.state && !!addressErrors?.state}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            fieldNote={touchedAddress?.state ? getFieldErrorMessage(addressErrors?.state) : undefined}
                            iconName={
                                touchedAddress?.state && !!addressErrors?.state ? 'exclamation-circle-fill' : undefined
                            }
                        />
                    </Grid.Item>
                </Grid>
            </Grid.Item>

            <Grid.Item>
                <TextField
                    data-testid="city"
                    data-cs-mask
                    wrapperTestId="city-field"
                    name="address.city"
                    type="text"
                    label="City"
                    value={addressValues.city}
                    isError={touchedAddress?.city && !!addressErrors?.city}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fieldNote={touchedAddress?.city ? getFieldErrorMessage(addressErrors?.city) : undefined}
                />
            </Grid.Item>
        </Grid>
    );
}

function AddressFormRHF({
    aptFieldOpen,
    countryTarget,
    statesOrProvinces,
    dropdownIsOpen,
    suggestedAddresses,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses,
    setAptFieldOpen,
    handleSuggestedAddressClick,
}: Readonly<RenderAddressFormProps>) {
    const {
        register,
        watch,
        setValue,
        formState: { errors, touchedFields, isSubmitted },
    } = useFormContext<AddressFormValues>();
    const addressValues = watch('address');
    const touchedAddress = touchedFields.address;
    const addressErrors = errors.address;
    const shouldShowFieldError = (fieldTouched: boolean | undefined, fieldError: unknown) =>
        !!fieldError && (isSubmitted || !!fieldTouched);
    const aptField = register('address.apt');
    const zipCodeField = register('address.zipCode');
    const stateField = register('address.state');
    const cityField = register('address.city');
    const stateErrorMessage = getFieldErrorMessage(addressErrors?.state);
    const hasUndefinedStateTypeError = stateErrorMessage === 'Invalid input: expected string, received undefined';
    let displayStateErrorMessage = stateErrorMessage;
    if (hasUndefinedStateTypeError) {
        displayStateErrorMessage = isLocaleCA() ? provinceRequiredMsg : stateRequiredMsg;
    }

    useEffect(() => {
        if (addressValues?.apt) {
            setAptFieldOpen(true);
        }
    }, [addressValues?.apt, setAptFieldOpen]);

    return (
        <Grid gap="sm">
            <Grid.Item>
                <StreetAddressLine
                    formLibrary="rhf"
                    dropdownIsOpen={dropdownIsOpen}
                    suggestedAddresses={suggestedAddresses}
                    handleSuggestedAddressClick={createSuggestedAddressSelectionHandler(
                        handleSuggestedAddressClick,
                        formattedAddress => {
                            setAptFieldOpen(!!formattedAddress.apt);
                            applyFormattedAddressToRHF(formattedAddress, setValue);
                        }
                    )}
                    loading={loading}
                    setLoading={setLoading}
                    checkAddressSuggestions={checkAddressSuggestions}
                    setDropdownIsOpen={setDropdownIsOpen}
                    setSuggestedAddresses={setSuggestedAddresses}
                />
            </Grid.Item>
            <Grid.Item>
                {aptFieldOpen ? (
                    <TextField
                        data-testid="apt"
                        data-cs-mask
                        name={aptField.name}
                        ref={aptField.ref}
                        type="text"
                        label="Apt/suite (optional)"
                        value={addressValues?.apt ?? ''}
                        onBlur={aptField.onBlur}
                        onChange={aptField.onChange}
                    />
                ) : (
                    <Button
                        data-testid="apt-btn"
                        aria-expanded="true"
                        className="sparky-u-width-fit-content sparky-u-typography-preset-7"
                        variant="link"
                        iconName="plus"
                        iconSize="md"
                        text="Add apt/suite/other"
                        type="button"
                        onClick={() => setAptFieldOpen(true)}
                    />
                )}
            </Grid.Item>

            <Grid.Item>
                <Grid variant="side-by-side" gap="sm">
                    <Grid.Item>
                        <TextField
                            data-testid="zipCode"
                            data-cs-mask
                            wrapperTestId="zipCode-field"
                            name={zipCodeField.name}
                            ref={zipCodeField.ref}
                            type="text"
                            label={isLocaleCA() ? 'Postal code' : 'Zip code'}
                            value={addressValues?.zipCode ?? ''}
                            isError={shouldShowFieldError(!!touchedAddress?.zipCode, addressErrors?.zipCode)}
                            onBlur={zipCodeField.onBlur}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                ev.target.value = removeSpecialCharacters(ev.target.value);
                                zipCodeSetter(ev, 'address.zipCode', (_, value) => {
                                    setValue('address.zipCode', value, { shouldDirty: true, shouldValidate: true });
                                });
                            }}
                            fieldNote={
                                shouldShowFieldError(!!touchedAddress?.zipCode, addressErrors?.zipCode)
                                    ? getFieldErrorMessage(addressErrors?.zipCode)
                                    : undefined
                            }
                            maxLength={countryTarget !== CountryTarget.BOTH && !isLocaleCA() ? 5 : undefined}
                        />
                    </Grid.Item>
                    <Grid.Item data-testid="state-field">
                        <SelectField
                            className={addressValues?.state ? 'sparky-is-filled' : undefined}
                            data-testid="state"
                            data-cs-mask
                            name={stateField.name}
                            items={statesOrProvinces?.map(({ abbr, name }) => ({
                                label: name,
                                value: abbr,
                                disabled: false,
                            }))}
                            label={isLocaleCA() ? 'Province' : 'State'}
                            selectedValue={addressValues?.state ?? ''}
                            value={addressValues?.state ?? ''}
                            isError={shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state)}
                            onBlur={stateField.onBlur}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                setValue('address.state', event.target.value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                    shouldTouch: true,
                                });
                            }}
                            fieldNote={
                                shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state)
                                    ? displayStateErrorMessage
                                    : undefined
                            }
                            iconName={
                                shouldShowFieldError(!!touchedAddress?.state, addressErrors?.state)
                                    ? 'exclamation-circle-fill'
                                    : undefined
                            }
                        />
                    </Grid.Item>
                </Grid>
            </Grid.Item>

            <Grid.Item>
                <TextField
                    data-testid="city"
                    data-cs-mask
                    wrapperTestId="city-field"
                    name={cityField.name}
                    ref={cityField.ref}
                    type="text"
                    label="City"
                    value={addressValues?.city ?? ''}
                    isError={shouldShowFieldError(!!touchedAddress?.city, addressErrors?.city)}
                    onBlur={cityField.onBlur}
                    onChange={cityField.onChange}
                    fieldNote={
                        shouldShowFieldError(!!touchedAddress?.city, addressErrors?.city)
                            ? getFieldErrorMessage(addressErrors?.city)
                            : undefined
                    }
                />
            </Grid.Item>
        </Grid>
    );
}
